import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import PatientService from '../../../services/patientService';
import DentalCertificateService from '../../../services/dentalCertificateService';

import './DentalCertificates.css';

const DentalCertificates = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to safely format dates
  const formatSafeDate = (dateValue, options = {}) => {
    if (!dateValue) return 'N/A';
    try {
      // Si es una fecha en formato YYYY-MM-DD, parsearlo directamente sin zona horaria
      if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateValue.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('es-ES', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          ...options
        });
      }
      
      // Si es una fecha ISO (2025-08-15T00:00:00.000Z), extraer solo la parte de la fecha
      if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) {
        const dateOnly = dateValue.split('T')[0]; // "2025-08-15"
        const [year, month, day] = dateOnly.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('es-ES', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          ...options
        });
      }
      
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...options
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatSafeDateTime = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      const dateStr = date.toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const timeStr = date.toLocaleTimeString('es-ES', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false
      });
      return `${dateStr} a las ${timeStr}`;
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return 'Invalid Date';
    }
  };

  const loadPatient = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const patientData = await PatientService.getPatientById(patientId, token);
      setPatient(patientData);
    } catch (error) {
      console.error('Error loading patient:', error);
      setError('Error al cargar información del paciente');
    }
  }, [patientId, auth.user?.access_token]);

  const loadCertificates = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;
      const response = await DentalCertificateService.getDentalCertificatesByPatient(patientId, token);
      const sortedCertificates = (response.certificates || []).sort((a, b) => {
        // Ordenar por fecha de creación (más recientes primero)
        const dateA = new Date(a.createdAt || a.date);
        const dateB = new Date(b.createdAt || b.date);
        return dateB - dateA;
      });
      setCertificates(sortedCertificates);
    } catch (error) {
      console.error('Error loading certificates:', error);
      setError('Error al cargar los certificados');
    } finally {
      setIsLoading(false);
    }
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    loadPatient();
    loadCertificates();
  }, [loadPatient, loadCertificates]);

  const handleNewCertificate = () => {
    navigate(`/patients/${patientId}/certificates/new`);
  };

  const handlePrintCertificate = (certificate) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateCertificateHTML(certificate));
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const handleDeleteCertificate = async (certificate) => {
    const dateStr = formatSafeDate(certificate.date);
    if (window.confirm(`¿Estás seguro de que deseas eliminar este certificado del ${dateStr}?`)) {
      try {
        const token = auth.user?.access_token;
        await DentalCertificateService.deleteDentalCertificate(certificate.id, token);
        // Reload certificates after deletion
        loadCertificates();
      } catch (error) {
        console.error('Error deleting certificate:', error);
        alert('Error al eliminar el certificado. Inténtalo de nuevo.');
      }
    }
  };

  const generateCertificateHTML = (certificate) => {
    const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
    const currentDate = new Date().toLocaleDateString('es-ES');
    const certificateDate = certificate.date ? new Date(certificate.date).toLocaleDateString('es-ES') : currentDate;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Certificado Dental - ${patientName}</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            margin: 20px;
            line-height: 1.4;
            color: #333;
            background: #fff;
          }
          .certificate-container {
            max-width: 700px;
            margin: 0 auto;
            border: 2px solid #2d3748;
            padding: 25px;
            position: relative;
            min-height: calc(11in - 40px);
            box-sizing: border-box;
          }
          .certificate-header {
            text-align: center;
            border-bottom: 1px solid #2d3748;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .clinic-logo {
            width: 60px;
            height: 60px;
            background: #f7fafc;
            border: 2px solid #2d3748;
            border-radius: 50%;
            margin: 0 auto 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: bold;
            color: #2d3748;
          }
          .clinic-info h1 {
            font-size: 22px;
            font-weight: bold;
            color: #2d3748;
            margin: 8px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .clinic-info h2 {
            font-size: 16px;
            color: #4a5568;
            margin: 3px 0;
            font-weight: normal;
          }
          .doctor-credentials {
            font-size: 12px;
            color: #718096;
            margin: 8px 0;
          }
          .certificate-title {
            font-size: 24px;
            font-weight: bold;
            color: #2d3748;
            margin: 15px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
            text-decoration: underline;
          }
          .certificate-content {
            font-size: 14px;
            line-height: 1.5;
            text-align: justify;
            margin: 20px 0;
            padding: 10px 0;
          }
          .patient-name {
            font-weight: bold;
            text-transform: uppercase;
            text-decoration: underline;
          }
          .signature-section {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
            align-items: end;
          }
          .date-section {
            text-align: left;
            font-size: 14px;
          }
          .signature-box {
            text-align: center;
            width: 200px;
          }
          .signature-line {
            border-top: 2px solid #2d3748;
            margin: 40px 0 10px 0;
          }
          .doctor-name {
            font-weight: bold;
            font-size: 14px;
            color: #2d3748;
          }
          .doctor-title {
            font-size: 12px;
            color: #4a5568;
            margin-top: 3px;
          }
          .certificate-footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
            text-align: center;
            color: #718096;
            font-size: 10px;
          }
          .contact-info {
            margin: 10px 0;
          }
          .certificate-number {
            position: absolute;
            top: 15px;
            right: 15px;
            font-size: 12px;
            color: #718096;
          }
          @media print {
            body { 
              margin: 0; 
              padding: 0;
            }
            .certificate-container { 
              margin: 15px;
              padding: 20px;
              break-inside: avoid;
              box-shadow: none;
              page-break-inside: avoid;
              max-height: calc(11in - 30px);
              overflow: hidden;
            }
            .signature-section {
              margin-top: 20px;
            }
            .certificate-footer {
              margin-top: 15px;
              font-size: 9px;
            }
          }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          <div class="certificate-number">
            Certificado No. ${certificate.id || 'XXXX'}
          </div>
          
          <div class="certificate-header">
            <div class="clinic-logo">
              🦷
            </div>
            <div class="clinic-info">
              <h1>Clínica Dental</h1>
              <h2>Dr. ${auth.user?.name || 'Nombre del Doctor'}</h2>
              <div class="doctor-credentials">
                Cirujano Dentista<br>
                Cédula Profesional: XXXXXXXXX
              </div>
            </div>
          </div>

          <h1 class="certificate-title">Certificado Dental</h1>

          <div class="certificate-content">
            <p>
              Por medio del presente documento, <strong>CERTIFICO</strong> que el/la paciente 
              <span class="patient-name">${patientName}</span>, 
              ${patient?.idNumber ? `con documento de identidad No. ${patient.idNumber},` : ''}
              ha sido examinado(a) y tratado(a) en esta clínica dental.
            </p>

            ${certificate.procedures && certificate.procedures.length > 0 ? `
              <p>
                <strong>Procedimiento${certificate.procedures.length > 1 ? 's' : ''} realizado${certificate.procedures.length > 1 ? 's' : ''}:</strong> 
                ${certificate.procedures.map(proc => `${proc.procedureName}${proc.quantity > 1 ? ` (${proc.quantity} ${proc.quantity > 1 ? 'sesiones' : 'sesión'})` : ''}`).join(', ')}.
              </p>
            ` : ''}

            ${certificate.observations ? `
              <p>
                <strong>Observaciones médicas:</strong> ${certificate.observations}
              </p>
            ` : ''}

            <p>
              Se extiende el presente certificado a solicitud del interesado para los fines que estime convenientes.
            </p>
          </div>

          <div class="signature-section">
            <div class="date-section">
              <p><strong>Fecha:</strong> ${certificateDate}</p>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="doctor-name">Dr. ${auth.user?.name || 'Nombre del Doctor'}</div>
              <div class="doctor-title">Cirujano Dentista</div>
              <div class="doctor-title">Cédula: XXXXXXXXX</div>
            </div>
          </div>

          <div class="certificate-footer">
            <div class="contact-info">
              <strong>Clínica Dental</strong><br>
              Dirección: Calle Principal #123, Ciudad<br>
              Teléfono: (XX) XXXX-XXXX | Email: contacto@clinica.com<br>
              www.clinicadental.com
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (error) {
    return (
      <div className="certificates-container">
        <PatientSectionHeader
          patientId={patientId}
          patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
          sectionTitle="Certificados Dentales"
        />
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="certificates-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Certificados Dentales"
        actionButton={
          <Button 
            variant="primary" 
            icon="fas fa-plus" 
            onClick={handleNewCertificate}
          >
            Nuevo Certificado
          </Button>
        }
      />

      <div className="certificates-content">
        {isLoading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <h3>Cargando certificados...</h3>
          </div>
        ) : certificates.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-certificate"></i>
            <h3>No hay certificados aún</h3>
            <p>Haz clic en "Nuevo Certificado" para crear el primer certificado dental de este paciente.</p>
          </div>
        ) : (
          <div className="certificates-list">
            {certificates.map((certificate) => (
              <div key={certificate.id} className="certificate-card">
                <div className="certificate-header">
                  <div className="certificate-date-section">
                    <div className="certificate-icon">
                      <i className="fas fa-certificate"></i>
                    </div>
                    <div className="date-info">
                      <div className="main-date">
                        <span className="date-label">Fecha del certificado:</span>
                        <span className="date-value">{formatSafeDate(certificate.date)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="certificate-actions">
                    <button
                      className="btn btn-info btn-icon btn-sm"
                      onClick={() => handlePrintCertificate(certificate)}
                      title="Imprimir"
                    >
                      <i className="fas fa-print"></i>
                    </button>
                    <button
                      className="btn btn-primary btn-icon btn-sm"
                      onClick={() => navigate(`/patients/${patientId}/certificates/${certificate.id}/edit`)}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn btn-danger btn-icon btn-sm"
                      onClick={() => handleDeleteCertificate(certificate)}
                      title="Eliminar"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>

                <div className="certificate-body">
                  <div className="certificate-procedures">
                    <label>Procedimientos:</label>
                    <div className="procedures-tags">
                      {certificate.procedures && certificate.procedures.length > 0 ? (
                        certificate.procedures.map((proc, index) => (
                          <span key={index} className="procedure-tag">
                            {proc.procedureName}
                            {proc.quantity > 1 && <span className="quantity">×{proc.quantity}</span>}
                          </span>
                        ))
                      ) : (
                        <span className="no-procedures">Sin procedimientos</span>
                      )}
                    </div>
                  </div>

                  {certificate.observations && (
                    <div className="certificate-observations">
                      <label>Observaciones:</label>
                      <p className="observations-text">
                        {certificate.observations}
                      </p>
                    </div>
                  )}

                  <div className="certificate-footer-info">
                    {certificate.doctorName && (
                      <div className="certificate-doctor">
                        <i className="fas fa-user-md"></i>
                        <span>Dr. {certificate.doctorName}</span>
                      </div>
                    )}
                    <div className="certificate-updated">
                      <i className="fas fa-clock"></i>
                      <span className="updated-label">Actualizado:</span>
                      <span className="updated-value">{formatSafeDateTime(certificate.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DentalCertificates;
